import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FilePlus, Loader2, Plus, Settings, Trash2 } from 'lucide-react';
import IndexWorkspaceShell from '@/components/index/IndexWorkspaceShell';
import AuditLogFeed from '@/components/index/AuditLogFeed';
import ScriptRegistryTable from '@/components/index/ScriptRegistryTable';
import StartProjectDialog from '@/components/StartProjectDialog';
import EditProjectMandateDialog from '@/components/EditProjectMandateDialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  listProjects,
  listProjectScripts,
  deleteProject,
  deleteScript,
} from '@/services/projectService';
import type { ScriptProject } from '@/types/scriptProject';
import type { WorkflowScriptListItem } from '@/types/workflow';
import {
  countByRecommendation,
  recommendationKpiTextClass,
  RECOMMENDATION_LABELS,
} from '@/config/recommendation';
import { echoScoreDisplay, passesDualClearance } from '@/config/indexThresholds';
import { normalizeMandate } from '@/config/developmentMandate';
import { formatMandateId } from '@/utils/formatIndexId';
import { summarizeMandateTags } from '@/utils/mandateSummary';
import { getActiveProjectId, setActiveProjectId } from '@/utils/projectWorkflowStorage';
import SlateDistributionHeatmap from '@/components/index/SlateDistributionHeatmap';
import { onIndexShortcut } from '@/hooks/useIndexShortcuts';

const WORKFLOW_SESSION_KEY = 'workflow_active_session_id';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ScriptProject[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(() => getActiveProjectId());

  const selectProject = useCallback((projectId: string) => {
    setSelectedProjectId(projectId);
    setActiveProjectId(projectId);
  }, []);
  const [scripts, setScripts] = useState<WorkflowScriptListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [scriptsLoading, setScriptsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showNewProject, setShowNewProject] = useState(false);
  const [mandateEditProject, setMandateEditProject] = useState<ScriptProject | null>(null);
  const [mandateSaveNotice, setMandateSaveNotice] = useState<string | null>(null);

  const loadProjects = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const list = await listProjects();
      setProjects(list);
      setSelectedProjectId((prev) => {
        if (prev && list.some((p) => p.id === prev)) return prev;
        const stored = getActiveProjectId();
        if (stored && list.some((p) => p.id === stored)) return stored;
        return list[0]?.id ?? null;
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load mandates');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadScripts = useCallback(async (projectId: string) => {
    setScriptsLoading(true);
    try {
      setScripts(await listProjectScripts(projectId));
    } catch {
      setScripts([]);
    } finally {
      setScriptsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    if (!selectedProjectId) {
      setScripts([]);
      return;
    }
    setActiveProjectId(selectedProjectId);
    void loadScripts(selectedProjectId);
  }, [selectedProjectId, loadScripts]);

  const startWorkflowForProject = useCallback(
    (projectId: string, sessionId?: string, newScript?: boolean) => {
      setActiveProjectId(projectId);
      if (sessionId) {
        sessionStorage.setItem(WORKFLOW_SESSION_KEY, sessionId);
      } else {
        sessionStorage.removeItem(WORKFLOW_SESSION_KEY);
      }
      const params = new URLSearchParams({ projectId });
      if (newScript) {
        params.set('newScript', '1');
      }
      navigate(`/workflow?${params.toString()}`);
    },
    [navigate],
  );

  useEffect(() => {
    return onIndexShortcut('audit-run', () => {
      if (selectedProjectId) {
        startWorkflowForProject(selectedProjectId);
      } else {
        navigate('/workflow');
      }
    });
  }, [selectedProjectId, navigate, startWorkflowForProject]);

  const selectedProject = useMemo(
    () => projects.find((p) => p.id === selectedProjectId) ?? null,
    [projects, selectedProjectId],
  );

  const kpi = useMemo(() => countByRecommendation(scripts), [scripts]);

  const scoreSummary = useMemo(() => {
    const audited = scripts.filter((s) => s.steps.analyse === 'complete');
    const echoValues = audited
      .map((s) => echoScoreDisplay(s.overall_score))
      .filter((v): v is number => v != null);
    const mandateValues = audited
      .map((s) => s.mandate_match_pct)
      .filter((v): v is number => v != null);
    return {
      avgEcho:
        echoValues.length > 0
          ? echoValues.reduce((a, b) => a + b, 0) / echoValues.length
          : null,
      avgMandate:
        mandateValues.length > 0
          ? mandateValues.reduce((a, b) => a + b, 0) / mandateValues.length
          : null,
      dualPass: audited.filter((s) =>
        passesDualClearance(s.overall_score, s.mandate_match_pct),
      ).length,
    };
  }, [scripts]);

  const handleScriptSelect = (script: WorkflowScriptListItem) => {
    if (!selectedProjectId) return;
    startWorkflowForProject(
      selectedProjectId,
      script.analysis_session_id || script.session_id,
    );
  };

  const handleIngestFirst = () => {
    if (!selectedProjectId) return;
    startWorkflowForProject(selectedProjectId, undefined, true);
  };

  const [confirm, setConfirm] = useState<{
    title: string;
    message: string;
    confirmLabel: string;
    onConfirm: () => Promise<void>;
  } | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  const requestDeleteMandate = (project: ScriptProject) => {
    setConfirm({
      title: `Delete mandate "${project.name}"?`,
      message:
        'This permanently deletes the mandate AND every script, scorecard, rebuild and uploaded ' +
        'file associated with it. This action is irreversible and cannot be undone.',
      confirmLabel: 'Delete mandate',
      onConfirm: async () => {
        await deleteProject(project.id);
        setConfirm(null);
        if (selectedProjectId === project.id) {
          setSelectedProjectId(null);
        }
        await loadProjects();
      },
    });
  };

  const requestDeleteScript = (script: WorkflowScriptListItem) => {
    const sessionId = script.analysis_session_id || script.session_id;
    if (!sessionId) return;
    setConfirm({
      title: `Delete script "${script.script_title || 'Untitled'}"?`,
      message:
        'This permanently deletes the script and all of its analysis, scorecard, rebuild data ' +
        'and uploaded files. This action is irreversible and cannot be undone.',
      confirmLabel: 'Delete script',
      onConfirm: async () => {
        await deleteScript(sessionId);
        setConfirm(null);
        const active = sessionStorage.getItem(WORKFLOW_SESSION_KEY);
        if (active === sessionId) {
          sessionStorage.removeItem(WORKFLOW_SESSION_KEY);
        }
        if (selectedProjectId) await loadScripts(selectedProjectId);
        await loadProjects();
      },
    });
  };

  const runConfirm = async () => {
    if (!confirm) return;
    setConfirmBusy(true);
    try {
      await confirm.onConfirm();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Deletion failed');
      setConfirm(null);
    } finally {
      setConfirmBusy(false);
    }
  };

  return (
    <IndexWorkspaceShell activeNav="home">
      <header className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="ni-label mb-2">Mandate command</p>
          <h1 className="text-2xl md:text-3xl font-sans font-bold text-white tracking-tight">
            Development Mandates
          </h1>
          <p className="mt-2 text-sm font-mono text-white/45 max-w-2xl">
            Forensic screenplay registry · Nuclius commissioning recommendations · board-ready evidence
          </p>
        </div>
        <Button variant="indexPrimary" size="sm" onClick={() => setShowNewProject(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create mandate
        </Button>
      </header>

      {loading && (
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-white/40" />
        </div>
      )}

      {error && (
        <div className="mb-6 ni-panel border-red-500/30 px-4 py-3 text-red-300 text-sm">{error}</div>
      )}

      {mandateSaveNotice && (
        <div className="mb-6 ni-panel border-emerald-500/30 px-4 py-3 text-emerald-300 text-sm">
          {mandateSaveNotice}
        </div>
      )}

      {!loading && selectedProject && (
        <>
          <section className="ni-panel p-5 md:p-6 mb-6">
            <p className="ni-label mb-2">Current mandate</p>
            <h2 className="text-xl md:text-2xl font-sans font-semibold text-white">
              {selectedProject.name}
            </h2>
            <p className="mt-2 text-xs font-mono text-white/45">
              {formatMandateId(selectedProject.id)} ·{' '}
              {summarizeMandateTags(normalizeMandate(selectedProject.development_mandate))} ·{' '}
              {selectedProject.script_count ?? scripts.length} SCRIPTS
            </p>
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 border-t border-[#2a2a2a] pt-6">
              <div>
                <p className="ni-kpi-value text-white">{kpi.active + kpi.queued}</p>
                <p className="ni-kpi-label">Active</p>
              </div>
              {RECOMMENDATION_LABELS.map((label) => (
                <div key={label}>
                  <p className={cn('ni-kpi-value', recommendationKpiTextClass(label))}>
                    {kpi[label.toLowerCase() as keyof typeof kpi]}
                  </p>
                  <p className="ni-kpi-label">{label}</p>
                </div>
              ))}
            </div>
            {scripts.some((s) => s.steps.analyse === 'complete') && (
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-4 border-t border-[#2a2a2a] pt-4">
                <div>
                  <p className="ni-kpi-value text-index-cleared tabular-nums">
                    {scoreSummary.avgEcho != null ? scoreSummary.avgEcho.toFixed(1) : '—'}
                  </p>
                  <p className="ni-kpi-label">Avg engagement (Echo)</p>
                </div>
                <div>
                  <p className="ni-kpi-value text-white tabular-nums">
                    {scoreSummary.avgMandate != null
                      ? `${scoreSummary.avgMandate.toFixed(1)}%`
                      : '—'}
                  </p>
                  <p className="ni-kpi-label">Avg mandate fit</p>
                </div>
                <div>
                  <p className="ni-kpi-value text-index-cleared">{scoreSummary.dualPass}</p>
                  <p className="ni-kpi-label">Dual pass (Echo + mandate)</p>
                </div>
              </div>
            )}
            <div className="mt-6">
              <SlateDistributionHeatmap scripts={scripts} />
            </div>
            {scripts.length === 0 && !scriptsLoading && (
              <div className="mt-6 pt-4 border-t border-[#2a2a2a]">
                <Button variant="indexOutline" size="sm" onClick={handleIngestFirst}>
                  Ingest first screenplay
                </Button>
              </div>
            )}
          </section>

          <section className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <p className="ni-label">Development mandates</p>
              <span className="text-[10px] font-mono text-white/35">
                {String(projects.length).padStart(2, '0')} REGISTERED
              </span>
            </div>
            <div className="ni-panel divide-y divide-[#1f1f1f]">
              {projects.map((project) => {
                const active = project.id === selectedProjectId;
                return (
                  <div
                    key={project.id}
                    className={cn(
                      'flex items-center gap-2 px-4 py-3 border-l-2 border-transparent',
                      active && 'ni-mandate-row-active',
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => selectProject(project.id)}
                      className="flex-1 min-w-0 text-left hover:opacity-90 transition-opacity"
                    >
                      <p className="text-xs font-mono text-white/70">
                        <span className="text-index-accent">{formatMandateId(project.id)}</span>
                        {' · '}
                        <span className="text-white/50">ACTIVE</span>
                        {' · '}
                        <span className="text-white">{project.name}</span>
                      </p>
                      <p className="text-[10px] font-mono text-white/35 mt-1 truncate">
                        {summarizeMandateTags(normalizeMandate(project.development_mandate))} ·{' '}
                        {project.script_count ?? 0} SCRIPTS
                      </p>
                    </button>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        type="button"
                        variant="indexPrimary"
                        size="sm"
                        className="h-8 px-2 text-[10px]"
                        onClick={() => {
                          selectProject(project.id);
                          startWorkflowForProject(project.id, undefined, true);
                        }}
                      >
                        <FilePlus className="h-3 w-3 mr-1" />
                        Add Script
                      </Button>
                      <Button
                        type="button"
                        variant="indexOutline"
                        size="sm"
                        className="h-8 px-2 text-[10px]"
                        onClick={() => setMandateEditProject(project)}
                      >
                        <Settings className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                      <Button
                        type="button"
                        variant="indexOutline"
                        size="sm"
                        className="h-8 px-2 text-[10px] text-red-300 hover:text-red-200 border-red-500/40"
                        onClick={() => requestDeleteMandate(project)}
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {scriptsLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-white/40" />
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <AuditLogFeed scripts={scripts} />
              <ScriptRegistryTable
                scripts={scripts}
                onSelect={handleScriptSelect}
                onDelete={requestDeleteScript}
              />
            </div>
          )}
        </>
      )}

      {!loading && projects.length === 0 && (
        <div className="ni-panel py-16 text-center">
          <p className="text-white/55 font-sans mb-4">No development mandates registered.</p>
          <Button variant="indexPrimary" onClick={() => setShowNewProject(true)}>
            Create mandate
          </Button>
        </div>
      )}

      <StartProjectDialog
        open={showNewProject}
        onOpenChange={setShowNewProject}
        onCreated={(project) => {
          void loadProjects();
          selectProject(project.id);
          startWorkflowForProject(project.id);
        }}
      />
      <EditProjectMandateDialog
        project={mandateEditProject}
        open={!!mandateEditProject}
        onOpenChange={(open) => {
          if (!open) setMandateEditProject(null);
        }}
        onSaved={(updated, reassessedScripts) => {
          setProjects((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
          setMandateEditProject(null);
          if (selectedProjectId === updated.id) {
            void loadScripts(updated.id);
          }
          const notice =
            reassessedScripts > 0
              ? `Mandate updated — ${reassessedScripts} script${reassessedScripts === 1 ? '' : 's'} reassessed.`
              : 'Mandate updated.';
          setMandateSaveNotice(notice);
          window.setTimeout(() => setMandateSaveNotice(null), 5000);
        }}
      />

      {confirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="ni-panel w-full max-w-md p-6 border-red-500/40">
            <h3 className="text-lg font-sans font-semibold text-white">{confirm.title}</h3>
            <p className="mt-3 text-sm font-mono text-white/60">{confirm.message}</p>
            <div className="mt-6 flex justify-end gap-3">
              <Button
                variant="indexOutline"
                size="sm"
                disabled={confirmBusy}
                onClick={() => setConfirm(null)}
              >
                Cancel
              </Button>
              <Button
                variant="indexPrimary"
                size="sm"
                disabled={confirmBusy}
                className="bg-red-600 hover:bg-red-500 text-white border-red-600"
                onClick={() => void runConfirm()}
              >
                {confirmBusy ? 'Deleting…' : confirm.confirmLabel}
              </Button>
            </div>
          </div>
        </div>
      )}
    </IndexWorkspaceShell>
  );
};

export default Home;
